import { View, Text } from 'react-native'
import { Image as ExpoImage } from 'expo-image'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../lib/hooks/use-theme'
import { useFontScale } from '../../lib/hooks/use-font-scale'
import { RADIUS, SPACING as BRAND_SPACING } from '../../lib/brand'
import { useSemanticColors, FONTS, TEXT_STYLES } from '../../lib/theme/tokens'
import {
  COVERAGE_LIVE_PARTNER,
  getCoveragePartnerClaim,
  isAuthInsurancePartnerTrustBadgeEnabled,
} from '../../lib/config/insurance-partners'

/**
 * Quién respalda los viajes, en la primera pantalla.
 *
 * Una fila compacta: la frase en voz baja y la marca de Quálitas a su
 * tamaño natural. La tarjeta de columna completa — filete, `space-between`
 * y un hueco muerto entre el texto y el logo — se leía como un anuncio
 * estirado, no como un crédito. Esto se pega al contenido y no inventa
 * aseguradoras.
 *
 * LA AFIRMACIÓN SE MUEVE SOLA. `getCoveragePartnerClaim()` cuelga del mismo
 * `COVERAGE_STATUS` que emite pólizas en el borde: mientras no emita, dice
 * de quién es la alianza; cuando emita, dice que cada viaje va asegurado.
 *
 * EL LOGO VA SOBRE BLANCO en los dos temas. Ver `COVERAGE_LIVE_PARTNER`.
 */

interface TrustBadgesProps {
  /** Optional override; defaults to the active theme. */
  isDark?: boolean
  /**
   * Horizontal placement of the compact claim + mark row.
   * Phone auth lockups use `'center'`; desktop editorial columns use `'start'`.
   */
  align?: 'start' | 'center'
}

/** Proporción del PNG oficial: 460 × 164. La altura se deriva, no se estima. */
const LOGO_WIDTH = 72
const LOGO_HEIGHT = Math.round((LOGO_WIDTH * 164) / 460)

export function TrustBadges({ isDark: isDarkProp, align = 'start' }: TrustBadgesProps = {}) {
  const { t } = useTranslation()
  const { isDark: isDarkTheme } = useTheme()
  const isDark = isDarkProp ?? isDarkTheme
  const colors = useSemanticColors(isDark)
  const { enlarged } = useFontScale()

  if (!isAuthInsurancePartnerTrustBadgeEnabled()) {
    return null
  }

  const isLive = getCoveragePartnerClaim() === 'per_trip'
  const claim = isLive
    ? t('insurance.provider_partner_live_title')
    : t('insurance.provider_partner_context')
  const a11yLabel = isLive
    ? t('insurance.provider_partner_live_a11y')
    : t('insurance.provider_partner_review_a11y')

  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: enlarged ? 'flex-start' : 'center',
        justifyContent: align === 'center' ? 'center' : 'flex-start',
        alignSelf: align === 'center' ? 'center' : 'flex-start',
        gap: BRAND_SPACING.sm,
      }}
      accessibilityRole="text"
      accessibilityLabel={a11yLabel}
      testID="auth.coverage-trust-badge"
    >
      <Text
        numberOfLines={enlarged ? 2 : 1}
        adjustsFontSizeToFit={!enlarged}
        minimumFontScale={0.86}
        style={{
          flexShrink: 1,
          fontFamily: FONTS.body,
          fontSize: TEXT_STYLES.caption.fontSize,
          lineHeight: TEXT_STYLES.caption.lineHeight,
          letterSpacing: TEXT_STYLES.caption.letterSpacing,
          color: colors.textSecondary,
        }}
      >
        {claim}
      </Text>

      {/* Placa blanca del logo, sin filete: en claro es invisible; en oscuro
          es el fondo para el que se dibujó el PNG. */}
      <View
        testID="auth.coverage-mark"
        style={{
          flexShrink: 0,
          paddingHorizontal: BRAND_SPACING.xs,
          paddingVertical: BRAND_SPACING.xs,
          borderRadius: RADIUS.sm,
          backgroundColor: COVERAGE_LIVE_PARTNER.plaqueColor,
        }}
      >
        <ExpoImage
          source={require('../../assets/images/insurer-qualitas-logo.png')}
          style={{ width: LOGO_WIDTH, height: LOGO_HEIGHT }}
          contentFit="contain"
          accessibilityLabel={COVERAGE_LIVE_PARTNER.legalName}
        />
      </View>
    </View>
  )
}
